import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AuroraBackground } from "@/components/AuroraBackground";
import { ThemeToggle } from "@/components/ThemeToggle";
import { MarkdownLite } from "@/components/MarkdownLite";
import { api } from "@/lib/api";
import { Cpu, ArrowLeft, Search, Loader2, AlertCircle } from "lucide-react";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://imperium-files.com").replace(/\/$/, "");
const EXAMPLES = ["P0401", "P2002", "P0299", "U0100", "P0234", "C1234"];

const upsert = (sel, make) => {
  let el = document.head.querySelector(sel);
  if (!el) { el = make(); document.head.appendChild(el); }
  return el;
};

const setMeta = (attr, key, content) => {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta"); m.setAttribute(attr, key); return m;
  });
  el.setAttribute("content", content);
};

const setJsonLd = (obj) => {
  const el = upsert('script[data-seo="obd-jsonld"]', () => {
    const s = document.createElement("script");
    s.type = "application/ld+json"; s.setAttribute("data-seo", "obd-jsonld"); return s;
  });
  el.textContent = obj ? JSON.stringify(obj) : "";
};

const setSeo = (code, desc, result = null) => {
  const title = code
    ? `${code} — Code défaut OBD-II : signification & solutions | Imperium`
    : "Décodeur de codes défauts OBD-II gratuit | Imperium";
  const description = desc || "Décodez gratuitement n'importe quel code défaut OBD-II (P, C, B, U) : signification, causes, symptômes et réparations.";
  const url = code ? `${SITE_URL}/obd/${code}` : `${SITE_URL}/obd`;
  document.title = title;
  setMeta("name", "description", description);
  // Canonical
  const link = upsert('link[rel="canonical"]', () => {
    const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l;
  });
  link.setAttribute("href", url);
  // Open Graph
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", url);
  // Structured data
  if (code && result) {
    setJsonLd({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "headline": `Code défaut OBD-II ${result.code}`,
          "description": description,
          "about": `Code diagnostic ${result.code} — ${result.system || ""}`.trim(),
          "inLanguage": "fr",
          "mainEntityOfPage": url,
          "author": { "@type": "Organization", "name": "Imperium · Automotive Files", "url": SITE_URL },
          "publisher": { "@type": "Organization", "name": "Imperium · Automotive Files", "url": SITE_URL },
        },
        {
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": `Que signifie le code défaut ${result.code} ?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `${result.code} concerne le système « ${result.system || "véhicule"} ». ${(result.explanation || "").replace(/[#*_`>]/g, "").replace(/\s+/g, " ").trim().slice(0, 320)}`,
            },
          }],
        },
      ],
    });
  } else {
    setJsonLd(null);
  }
};

export const ObdDecoder = () => {
  const navigate = useNavigate();
  const { code: paramCode } = useParams();
  const [code, setCode] = useState(paramCode ? paramCode.toUpperCase() : "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookup = useCallback(async (c, fromUrl = false) => {
    const q = (c || "").trim().toUpperCase();
    if (!q) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/public/obd/${q}`);
      setResult(data);
      setSeo(data.code, `Code défaut OBD-II ${data.code} (${data.system}) : signification, causes probables, symptômes et réparations courantes.`, data);
      if (!fromUrl) navigate(`/obd/${q}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Code introuvable");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (paramCode) { setCode(paramCode.toUpperCase()); lookup(paramCode, true); }
    else setSeo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramCode]);

  return (
    <div className="relative min-h-screen text-white">
      <AuroraBackground />
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-12">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">
            <ArrowLeft className="w-4 h-4" /> Accueil
          </button>
          <div className="flex items-center gap-2">
            <span className="brand-word font-display font-bold tracking-widest">IMPERIUM</span>
            <ThemeToggle className="ml-2" />
          </div>
        </div>

        <div className="text-center mb-10">
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">// Outil gratuit</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Décodeur de codes défauts OBD-II</h1>
          <p className="mt-4 text-zinc-400 max-w-lg mx-auto text-sm">Entrez un code défaut (ex. P0401) pour obtenir sa signification, ses causes probables et les réparations courantes.</p>
        </div>

        <div className="glass rounded-md p-2 flex items-center gap-2 mb-4">
          <input
            data-testid="obd-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && lookup(code)}
            placeholder="P0401"
            className="flex-1 bg-transparent px-4 py-3 font-mono uppercase tracking-widest text-white focus:outline-none placeholder:text-zinc-600"
          />
          <button data-testid="obd-search-btn" onClick={() => lookup(code)} disabled={loading} className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Décoder
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest py-1">Exemples :</span>
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => { setCode(ex); lookup(ex); }} className="font-mono text-[11px] px-2.5 py-1 border border-white/10 rounded-sm text-zinc-400 hover:border-aurora-cyan/40 hover:text-aurora-cyan transition-colors">{ex}</button>
          ))}
        </div>

        {error && (
          <div className="glass rounded-md p-4 flex items-center gap-3 border-l-2 border-signal-error mb-6">
            <AlertCircle className="w-5 h-5 text-signal-error" />
            <span className="font-mono text-xs text-zinc-300">{error}</span>
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-md p-7" data-testid="obd-result">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="font-display text-3xl font-bold text-aurora-cyan">{result.code}</div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{result.type}</div>
                <div className="font-mono text-xs text-zinc-300">{result.system}</div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-5">
              <MarkdownLite text={result.explanation} />
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-zinc-500">Ce code concerne une suppression DTC ? Nous pouvons traiter votre fichier.</p>
              <button onClick={() => navigate("/login")} data-testid="obd-cta-btn" className="px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cyan-300 transition-colors">
                Traiter mon fichier
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
